/**
 * Browser-side API client. Talks only to this app's own `/api/*` proxy — never
 * to ps-integrations directly, and never holds the bearer key.
 */

import type { ExecutionResult } from "graphql";
import type { ConfigDocument, CopyReport } from "./subscriberConfig";
import type {
  AiUsageResponse,
  DeleteCustomFieldMappingLookup,
  DeleteCustomFieldMappingResponse,
  DevAccount,
  EnableInteractionAssigneeImportInput,
  EnableInteractionAssigneeImportResult,
  ExtensionStoreSummary,
  FieldsQuery,
  FieldsResponse,
  FilterCounts,
  HealthResponse,
  IntegrationStatusesResponse,
  LlmPromptOverride,
  LlmPromptPurpose,
  LlmPromptView,
  LockedFieldResource,
  LockedFieldsView,
  MappingBlob,
  MappingEnvelope,
  MappingResult,
  MappingType,
  SalesforceConfig,
  SalesforceConfigInput,
  SalesforceCustomFieldMapping,
  SalesforceFilter,
  SavedFiltersResponse,
  SetCustomFieldEditableResponse,
  SoqlRequest,
  SoqlResponse,
  SubscriberSummary,
} from "./types";

/** Thrown for any non-2xx response; carries the upstream `error` + status. */
export class ApiRequestError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = "ApiRequestError";
  }
}

async function request<T>(url: string, init?: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await fetch(url, init);
  } catch (err) {
    throw new ApiRequestError(
      `Network error: ${err instanceof Error ? err.message : String(err)}`,
      0,
    );
  }

  const text = await res.text();
  let data: unknown;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      // Non-JSON body (shouldn't happen via the proxy) — surface raw text.
      if (!res.ok) throw new ApiRequestError(text, res.status);
    }
  }

  if (!res.ok) {
    const body = data as
      | {
          error?: string;
          errors?: unknown;
          loginUrl?: string;
          devAuthRequired?: boolean;
        }
      | undefined;
    // 401 means the session cookie is missing/expired/invalid.
    // if the API returns a login URL, send the user there.
    if (res.status === 401 && body?.loginUrl) {
      window.location.href = body.loginUrl;
    }
    // Dev-auth mode has no login redirect to send the user to — the picked
    // dev user cookie was missing/cleared, so just reload; App re-checks
    // /api/health and shows the picker again.
    if (res.status === 401 && body?.devAuthRequired) {
      window.location.reload();
    }
    const message =
      body?.error ??
      (body?.errors != null
        ? JSON.stringify(body.errors)
        : `Request failed (${res.status})`);
    throw new ApiRequestError(message, res.status);
  }
  return data as T;
}

export async function getHealth(): Promise<HealthResponse> {
  return request("/api/health");
}

/** Search subscribers by name (or a pasted UUID/global ID) for the autocomplete. */
export async function searchSubscribers(
  query: string,
): Promise<SubscriberSummary[]> {
  const res = await request<{ subscribers: SubscriberSummary[] }>(
    `/api/subscribers?q=${encodeURIComponent(query)}`,
  );
  return res.subscribers;
}

/**
 * The extension stores in the ps-main directory (ADR-26), newest first.
 * Pass a subscriber UUID to narrow the listing to one org.
 */
export async function getExtensionStores(
  subscriberId?: string,
): Promise<ExtensionStoreSummary[]> {
  const query = subscriberId
    ? `?subscriber=${encodeURIComponent(subscriberId)}`
    : "";
  const res = await request<{ stores: ExtensionStoreSummary[] }>(
    `/api/extension-stores${query}`,
  );
  return res.stores;
}

/** Every AI usage event in the last `days` days, all subscribers (ps-main). */
export async function getAiUsage(days: number): Promise<AiUsageResponse> {
  return request(`/api/ai-usage?days=${days}`);
}

/** Product default + this subscriber's override for one purpose (ps-main). */
export async function getLlmPrompt(
  subscriberId: string,
  purpose: LlmPromptPurpose,
): Promise<LlmPromptView> {
  return request(
    `/api/subscribers/${subscriberId}/llm-prompt?purpose=${encodeURIComponent(purpose)}`,
  );
}

/** Replace a subscriber's LLM prompt override for one purpose (ps-main). */
export async function updateLlmPromptOverride(
  subscriberId: string,
  purpose: LlmPromptPurpose,
  prompt: string,
): Promise<LlmPromptOverride> {
  return request(`/api/subscribers/${subscriberId}/llm-prompt-overrides`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ purpose, prompt }),
  });
}

/** This subscriber's locked fields for one resource, and what may be locked (ps-main). */
export async function getLockedFields(
  subscriberId: string,
  resource: LockedFieldResource,
): Promise<LockedFieldsView> {
  return request(
    `/api/subscribers/${subscriberId}/locked-fields?resource=${encodeURIComponent(resource)}`,
  );
}

/**
 * Replace this subscriber's locked fields for one resource (ps-main).
 *
 * `fieldNames` is the complete set to keep. A field it omits becomes editable.
 */
export async function putLockedFields(
  subscriberId: string,
  resource: LockedFieldResource,
  fieldNames: string[],
): Promise<string[]> {
  const res = await request<{ lockedFields: string[] }>(
    `/api/subscribers/${subscriberId}/locked-fields`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ resource, fieldNames }),
    },
  );
  return res.lockedFields;
}

/**
 * Run one GraphQL operation against platform-service-main, as the logged-in
 * user. Backs the GraphQL console (see components/GraphqlConsole.tsx) and has
 * the signature GraphiQL's `fetcher` prop wants.
 *
 * A resolver error arrives as a normal 200 with an `errors` array, which the
 * console renders in the response pane. Only a transport, auth, or config
 * failure throws.
 */
export async function runGraphql(params: {
  query: string;
  operationName?: string | null;
  variables?: unknown;
}): Promise<ExecutionResult> {
  return request("/api/graphql", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(params),
  });
}

// ---------------------------------------------------------------------------
// Dev-auth account picker (local dev only — see lib/auth.ts)
// ---------------------------------------------------------------------------

/** List ps-main's debug accounts to choose from. */
export async function getDevAccounts(): Promise<DevAccount[]> {
  const res = await request<{ accounts: DevAccount[] }>("/api/dev/accounts");
  return res.accounts;
}

/** Sign in as the given dev account (sets mechanicus's own session cookie). */
export async function selectDevAccount(id: string): Promise<void> {
  await request("/api/dev/session", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ id }),
  });
}

/** Sign out of the current dev account so a different one can be picked. */
export async function clearDevAccount(): Promise<void> {
  await request("/api/dev/session", { method: "DELETE" });
}

export async function getIntegrationStatus(
  subscriberId: string,
): Promise<IntegrationStatusesResponse> {
  return request(`/api/config/${subscriberId}/integrations`);
}

/** Read the whole Salesforce config (secret elided; see `client_secret_set`). */
export async function getSalesforceConfig(
  subscriberId: string,
): Promise<SalesforceConfig> {
  return request(`/api/config/${subscriberId}/salesforce`);
}

/**
 * Update the Salesforce config. Read-modify-write server-side: only the fields
 * present in `input` change. Omit `client_secret` to keep the stored one.
 */
export async function putSalesforceConfig(
  subscriberId: string,
  input: SalesforceConfigInput,
): Promise<void> {
  await request(`/api/config/${subscriberId}/salesforce`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
}

/** Read the subscriber's currently-stored import filters. */
export async function getSavedFilters(
  subscriberId: string,
): Promise<SalesforceFilter[]> {
  const res = await request<SavedFiltersResponse>(
    `/api/config/${subscriberId}/salesforce/filters/saved`,
  );
  return res.filters ?? [];
}

/** Preview live Account/Contact counts for a filter set without persisting. */
export async function previewFilters(
  subscriberId: string,
  filters: SalesforceFilter[],
): Promise<FilterCounts> {
  return request(`/api/config/${subscriberId}/salesforce/filters/preview`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ filters }),
  });
}

/** Persist a filter set. Returns the live counts computed on save. */
export async function saveFilters(
  subscriberId: string,
  filters: SalesforceFilter[],
): Promise<FilterCounts> {
  return request(`/api/config/${subscriberId}/salesforce/filters`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ filters }),
  });
}

export async function getFields(
  subscriberId: string,
  objectName: string,
  query: FieldsQuery = {},
): Promise<FieldsResponse> {
  const params = new URLSearchParams();
  if (query.include_references !== undefined)
    params.set("include_references", String(query.include_references));
  if (query.refresh !== undefined) params.set("refresh", String(query.refresh));
  const qs = params.toString();
  return request(
    `/api/sf/${subscriberId}/objects/${encodeURIComponent(objectName)}/fields${
      qs ? `?${qs}` : ""
    }`,
  );
}

/**
 * Trigger a Salesforce import.
 *
 * - Incremental: pass the subscriber's last sync (`last_import_started_at`) as
 *   `lookback_date` so the workflow uses that start datetime override.
 * - Full: pass an early date (e.g. "1900-01-01") to re-sync everything.
 * - `resources`: restrict the run (intersected with the subscriber's
 *   configured `resources_to_import`). Scoped runs do not advance the
 *   last_import_started_at watermark.
 */
export async function triggerImport(
  subscriberId: string,
  opts?: { lookbackDate?: string; resources?: string[] },
): Promise<unknown> {
  const body: Record<string, unknown> = {};
  if (opts?.lookbackDate) body.lookback_date = opts.lookbackDate;
  if (opts?.resources && opts.resources.length > 0) {
    body.resources = opts.resources;
  }
  return request(`/api/sf/${subscriberId}/import`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export interface BatchExportResult {
  workflow_id: string;
  run_id: string;
}

/**
 * Trigger a Salesforce batch export. `resources` must be a non-empty list of
 * batch-exportable names (today: constituent_assignees). `dryRun` defaults to
 * true server-side; pass false to write to Salesforce.
 */
export async function triggerBatchExport(
  subscriberId: string,
  opts: { resources: string[]; dryRun?: boolean },
): Promise<BatchExportResult> {
  return request(`/api/sf/${subscriberId}/export`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      resources: opts.resources,
      dry_run: opts.dryRun ?? true,
    }),
  });
}

/**
 * Enable interaction-assignee import: resolve Task/Event User lookups, persist
 * mappings, opt interactions.assignees into resource_configuration (dry_run),
 * and start SalesforceIncrementalImportWorkflow.
 *
 * POST …/config/<id>/salesforce/interaction-assignee-import/
 */
export async function enableInteractionAssigneeImport(
  subscriberId: string,
  body: EnableInteractionAssigneeImportInput,
): Promise<EnableInteractionAssigneeImportResult> {
  return request(
    `/api/config/${subscriberId}/salesforce/interaction-assignee-import`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );
}

/** List active SF ↔ Instil custom-field mappings for a subscriber. */
export async function getCustomFieldMappings(
  subscriberId: string,
): Promise<SalesforceCustomFieldMapping[]> {
  return request(`/api/sf/${subscriberId}/custom-field-mappings`);
}

/**
 * Soft-delete one custom-field mapping. Identify by Instil CF id, or by SF
 * field name + object. Returns on success; throws ApiRequestError on 4xx/5xx.
 */
export async function deleteCustomFieldMapping(
  subscriberId: string,
  lookup: DeleteCustomFieldMappingLookup,
): Promise<DeleteCustomFieldMappingResponse> {
  const params = new URLSearchParams();
  if ("instilCustomFieldId" in lookup) {
    params.set("instil_custom_field_id", lookup.instilCustomFieldId);
  } else {
    params.set(
      "salesforce_custom_field_name",
      lookup.salesforceCustomFieldName,
    );
    params.set("salesforce_object_name", lookup.salesforceObjectName);
  }
  return request(
    `/api/sf/${subscriberId}/custom-field-mappings?${params.toString()}`,
    { method: "DELETE" },
  );
}

/**
 * Set whether an Instil custom field definition can be edited in the product.
 * Proxies to ps-main's worker service. Rows that share this Instil id share
 * the flag.
 */
export async function setCustomFieldEditable(
  subscriberId: string,
  customFieldId: string,
  isEditable: boolean,
): Promise<SetCustomFieldEditableResponse> {
  return request(`/api/sf/${subscriberId}/custom-fields/${customFieldId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ is_editable: isEditable }),
  });
}

export async function runSoql(
  subscriberId: string,
  body: SoqlRequest,
): Promise<SoqlResponse> {
  return request(`/api/sf/${subscriberId}/soql`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export async function getMapping(
  subscriberId: string,
  mappingType: MappingType,
): Promise<MappingResult> {
  const env = await request<MappingEnvelope>(
    `/api/config/${subscriberId}/mappings/${mappingType}`,
  );
  return { source: env.data.source, mapping: env.data.mapping };
}

export async function putMapping(
  subscriberId: string,
  mappingType: MappingType,
  blob: MappingBlob,
): Promise<MappingBlob> {
  return request(`/api/config/${subscriberId}/mappings/${mappingType}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(blob),
  });
}

/** Result of asking the Claude agent to revise a mapping. */
export interface MappingAssistResult {
  summary: string;
  mapping: MappingBlob;
  warnings: string[];
}

/**
 * Ask a Claude Code agent (with the ps-integrations repo on disk) to revise the
 * given mapping per a natural-language request. Slow (the agent reads the repo);
 * callers should show a loading state. Returns a *proposal* — nothing is saved.
 */
export async function assistMapping(
  mappingType: MappingType,
  currentMapping: MappingBlob,
  requestText: string,
): Promise<MappingAssistResult> {
  return request(`/api/mappings/assist`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ mappingType, currentMapping, request: requestText }),
  });
}

// ---------------------------------------------------------------------------
// Subscriber configuration copy (sandbox to production)
// ---------------------------------------------------------------------------

/** Read a subscriber's configuration into a document. */
export async function exportSubscriberConfig(
  subscriberId: string,
  includeSalesforce: boolean,
): Promise<ConfigDocument> {
  return request("/api/subscriber-config/export", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ subscriberId, includeSalesforce }),
  });
}

/**
 * Write an export document onto a target subscriber. `dryRun` writes nothing.
 *
 * `copyLabels` adds the labels of the source. The Salesforce import makes a
 * label out of each child campaign, so the labels of a sandbox subscriber name
 * the campaigns of the sandbox org. The copy leaves them alone by default.
 */
export async function copySubscriberConfig(
  subscriberId: string,
  document: ConfigDocument,
  dryRun: boolean,
  copyLabels: boolean,
): Promise<CopyReport> {
  return request("/api/subscriber-config/copy", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ subscriberId, document, dryRun, copyLabels }),
  });
}

/**
 * Write the Salesforce configuration of the document onto the target.
 *
 * The 3 connection fields belong to the production org, so the caller gives
 * them. Every other setting comes from the document.
 */
export async function copySalesforceConfig(
  subscriberId: string,
  document: ConfigDocument,
  credentials: { auth_url: string; client_id: string; client_secret: string },
  dryRun: boolean,
): Promise<CopyReport> {
  return request("/api/subscriber-config/salesforce", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ subscriberId, document, credentials, dryRun }),
  });
}
